/**
 * Общие вкладки:
 *   ThemeTab («Дизайн») — ОДНА галерея шаблонов (каждый = готовый виджет) +
 *                         панель переменных (цвет/шрифт/размеры/радиус) + свой CSS.
 *   BehaviorTab — режим показа, позиция, авто-показ, привязка к кнопке сайта.
 *   CodeTab — сниппет встраивания.
 * Источник шаблонов — единый реестр меты @inbx/shared.
 */
import { templateMetaByType, type WidgetType } from '@inbx/shared/schemas';
import { widgetSnippet } from '@inbx/shared/widget';
import { useState } from 'react';

import { apiClient } from '../api/client';
import { useAuthStore } from '../auth/auth-store';

import { type WidgetDraft } from './editor-shell';

/**
 * Загрузка ассета на НАШ сервер (изолированно по воркспейсу) вместо сырого URL —
 * закрывает XSS/эксфильтрацию через произвольные внешние ссылки. Возвращает
 * абсолютный URL нашего origin. (Глубокая санитизация — следующей итерацией.)
 */
export function AssetUpload({
  value,
  onChange,
  accept,
}: {
  value?: string | null;
  onChange: (url: string | null) => void;
  accept: string;
}): JSX.Element {
  const slug = useAuthStore((s) => s.user?.workspaceSlug);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pick = async (f: File): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      const r = await apiClient.upload<{ url: string }>('/widgets/assets', f, { workspaceSlug: slug });
      onChange(r.url);
    } catch (e) {
      setErr((e as Error).message || 'Ошибка загрузки');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      {value && (
        <div className="inbx-editor-help" style={{ marginBottom: 6 }}>
          Загружено:{' '}
          <a href={value} target="_blank" rel="noreferrer">
            файл
          </a>{' '}
          <button type="button" className="inbx-btn inbx-btn--ghost" onClick={() => onChange(null)}>
            убрать
          </button>
        </div>
      )}
      <input
        type="file"
        accept={accept}
        disabled={busy}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void pick(f);
        }}
      />
      {busy && <span className="inbx-editor-help"> загрузка…</span>}
      {err && <div className="inbx-editor-help" style={{ color: 'var(--inbx-danger)' }}>{err}</div>}
    </div>
  );
}

interface PatchProps {
  draft: WidgetDraft;
  onChange: (patch: Partial<WidgetDraft>) => void;
}

const VARS: Array<{ key: string; label: string; type: 'color' | 'text'; placeholder?: string }> = [
  { key: '--inbx-primary', label: 'Основной цвет', type: 'color' },
  { key: '--inbx-accent', label: 'Акцент', type: 'color' },
  { key: '--inbx-text', label: 'Цвет текста', type: 'color' },
  { key: '--inbx-bg', label: 'Фон', type: 'color' },
  { key: '--inbx-border', label: 'Граница', type: 'color' },
  { key: '--inbx-font', label: 'Шрифт', type: 'text', placeholder: 'system-ui, sans-serif' },
  { key: '--inbx-fs', label: 'Размер шрифта', type: 'text', placeholder: '14px' },
  { key: '--inbx-radius', label: 'Радиус', type: 'text', placeholder: '10px' },
];

export function ThemeTab({ draft, onChange }: PatchProps): JSX.Element {
  const type = draft.type as WidgetType;
  const templates = templateMetaByType(type);
  const theme = (draft.themeConfig ?? {}) as { template_id?: string; vars?: Record<string, string> };
  const vars = theme.vars ?? {};

  const pickTemplate = (id: string): void => {
    // Выбор дизайна меняет только шаблон — контент (тексты/поля) не трогаем.
    onChange({ themeConfig: { ...theme, template_id: id } });
  };
  const setVar = (key: string, value: string): void => {
    const next = { ...vars };
    if (value) next[key] = value;
    else delete next[key];
    onChange({ themeConfig: { ...theme, vars: next } });
  };

  return (
    <div>
      <div className="inbx-editor-section">
        <div className="inbx-editor-section__title">Шаблон (готовый дизайн)</div>
        <div className="inbx-theme-picker">
          {templates.map((t) => (
            <div
              key={t.id}
              className={`inbx-theme-card ${theme.template_id === t.id ? 'is-active' : ''}`}
              onClick={() => pickTemplate(t.id)}
            >
              <div className="inbx-theme-card__name">{t.name}</div>
              <div className="inbx-theme-card__desc">{t.description}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="inbx-editor-section">
        <div className="inbx-editor-section__title">Переменные оформления</div>
        <div className="inbx-editor-help" style={{ marginBottom: 10 }}>
          Накладываются поверх выбранного шаблона. Можно также подключить свой CSS ниже.
        </div>
        <div className="inbx-editor-row" style={{ flexWrap: 'wrap', gap: 12 }}>
          {VARS.map((v) => (
            <div key={v.key} style={{ flex: v.type === 'color' ? '0 0 30%' : '1 1 45%' }}>
              <label className="inbx-editor-label">{v.label}</label>
              <input
                type={v.type === 'color' ? 'color' : 'text'}
                className="inbx-editor-input"
                placeholder={v.placeholder}
                value={vars[v.key] ?? (v.type === 'color' ? '#2563eb' : '')}
                onChange={(e) => setVar(v.key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="inbx-editor-section">
        <div className="inbx-editor-section__title">Свой CSS (необязательно)</div>
        <AssetUpload
          accept=".css,text/css"
          value={draft.customCssUrl}
          onChange={(url) => onChange({ customCssUrl: url })}
        />
        <div className="inbx-editor-help">
          Файл .css загружается на наш сервер и подключается ПОСЛЕ шаблона/переменных.
        </div>
      </div>
    </div>
  );
}

const CORNERS: Array<{ value: string; label: string }> = [
  { value: 'top-left', label: '↖ Сверху слева' },
  { value: 'top-right', label: '↗ Сверху справа' },
  { value: 'bottom-left', label: '↙ Снизу слева' },
  { value: 'bottom-right', label: '↘ Снизу справа' },
];

export function BehaviorTab({ draft, onChange }: PatchProps): JSX.Element {
  const b = (draft.behaviorConfig ?? {}) as {
    mode?: 'popup' | 'inline' | 'modal';
    position?: string;
    auto_open_after?: number;
    dismissible?: boolean;
    show_once_per?: 'session' | 'day' | 'week' | 'forever';
    open_on_selector?: string;
  };
  const set = (patch: Partial<typeof b>): void => onChange({ behaviorConfig: { ...draft.behaviorConfig, ...patch } });

  return (
    <div>
      <div className="inbx-editor-section">
        <div className="inbx-editor-section__title">Способ показа</div>
        <select
          className="inbx-editor-select"
          value={b.mode ?? 'popup'}
          onChange={(e) => set({ mode: e.target.value as 'popup' | 'inline' | 'modal' })}
        >
          <option value="popup">Popup — кнопка в углу, окно по клику</option>
          <option value="inline">Inline — встроен в страницу</option>
          <option value="modal">Modal — центрированное окно с затемнением</option>
        </select>
      </div>

      {b.mode !== 'inline' && (
        <div className="inbx-editor-section">
          <div className="inbx-editor-section__title">Позиция</div>
          <div className="inbx-corner-grid">
            {CORNERS.map((p) => (
              <button
                key={p.value}
                type="button"
                className={`inbx-corner-cell ${(b.position ?? 'bottom-right') === p.value ? 'is-active' : ''}`}
                onClick={() => set({ position: p.value })}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="inbx-editor-section">
        <div className="inbx-editor-section__title">Открытие</div>
        <div className="inbx-editor-row">
          <div>
            <label className="inbx-editor-label">Авто-показ через N секунд</label>
            <input
              type="number"
              min={0}
              className="inbx-editor-input"
              value={b.auto_open_after ?? 0}
              onChange={(e) => set({ auto_open_after: Number(e.target.value) || 0 })}
            />
          </div>
          <div>
            <label className="inbx-editor-label">Открыть по клику на элемент сайта</label>
            <input
              type="text"
              className="inbx-editor-input"
              placeholder="#callback-btn, .order-call"
              value={b.open_on_selector ?? ''}
              onChange={(e) => set({ open_on_selector: e.target.value })}
            />
            <div className="inbx-editor-help">CSS-селектор кнопки (для «обратного звонка» и т.п.)</div>
          </div>
        </div>
        <div className="inbx-editor-row" style={{ marginTop: 8 }}>
          <div>
            <label className="inbx-editor-label">Частота показа</label>
            <select
              className="inbx-editor-select"
              value={b.show_once_per ?? 'session'}
              onChange={(e) => set({ show_once_per: e.target.value as 'session' | 'day' | 'week' | 'forever' })}
            >
              <option value="session">Раз за сессию</option>
              <option value="day">Раз в день</option>
              <option value="week">Раз в неделю</option>
              <option value="forever">Только один раз</option>
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', alignSelf: 'flex-end' }}>
            <input type="checkbox" checked={b.dismissible ?? true} onChange={(e) => set({ dismissible: e.target.checked })} />
            Можно закрыть
          </label>
        </div>
      </div>
    </div>
  );
}

export function CodeTab({ widgetPublicKey, workspaceId }: { widgetPublicKey: string; workspaceId: string }): JSX.Element {
  // Адрес абсолютный: код вставляют на ЧУЖОЙ сайт, и относительный путь
  // указывал бы на сам этот сайт. Админка и сервер живут на одном адресе
  // (в проде — за одним nginx, в разработке — через прокси Vite).
  const snippet = widgetSnippet(window.location.origin, workspaceId, widgetPublicKey);
  return (
    <div>
      <div className="inbx-editor-section">
        <div className="inbx-editor-section__title">Код для встраивания</div>
        <div className="inbx-editor-help" style={{ marginBottom: 8 }}>
          Вставьте перед <code>&lt;/body&gt;</code> на странице сайта.
        </div>
        <textarea
          className="inbx-editor-textarea"
          rows={3}
          readOnly
          value={snippet}
          onClick={(e) => (e.target as HTMLTextAreaElement).select()}
        />
      </div>
    </div>
  );
}

/**
 * Подпись посетителей для своего сайта (на любом движке).
 *
 * Сайт знает, кто у него вошёл; чтобы чат этому поверил и оператор сразу
 * увидел заказы покупателя, сервер сайта подписывает данные секретом отсюда.
 * Секрет показывается один раз: выпуск нового отменяет старый.
 * Плагин WordPress и платформа продаж получают его сами при подключении.
 */
export function IdentitySecretSection({
  widgetId,
  workspaceSlug,
}: {
  widgetId: string;
  workspaceSlug: string | undefined;
}): JSX.Element {
  const [secret, setSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function issue(): Promise<void> {
    setError(null);
    setPending(true);
    try {
      const res = await apiClient.post<{ identity_secret: string }>(
        `/partner/widgets/${widgetId}/identity-secret`,
        {},
        { workspaceSlug },
      );
      setSecret(res.identity_secret);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'не удалось выпустить секрет');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="inbx-editor-section">
      <div className="inbx-editor-section__title">Подпись посетителей</div>
      <div className="inbx-editor-help" style={{ marginBottom: 8 }}>
        Чтобы оператор сразу видел заказы вошедшего покупателя, сервер сайта передаёт в чат
        подписанные данные: <code>inbx(&apos;identify&apos;, {'{'} id, email, phone, name, hash {'}'})</code>,
        где <code>hash = HMAC-SHA256(секрет, «v1|id|почта в нижнем регистре|цифры телефона»)</code>.
        Без подписи данным посетителя чат не доверяет.
      </div>
      {secret ? (
        <>
          <textarea
            className="inbx-editor-textarea"
            rows={2}
            readOnly
            value={secret}
            onClick={(e) => (e.target as HTMLTextAreaElement).select()}
          />
          <div className="inbx-editor-help">
            Сохраните его на сервере сайта — здесь он больше не покажется. В браузер секрет не
            передавайте.
          </div>
        </>
      ) : (
        <button type="button" className="inbx-btn" onClick={() => void issue()} disabled={pending}>
          {pending ? 'Выпускаем…' : 'Выпустить секрет'}
        </button>
      )}
      {error && <div style={{ color: 'var(--inbx-danger, #dc2626)', marginTop: 6 }}>{error}</div>}
    </div>
  );
}
